/**
 * Department Service
 * 
 * Service layer for department-related API operations.
 * Provides abstraction over API calls and handles data transformation.
 * 
 * @module DepartmentService
 */

import { getDepartments, getDepartmentById, createDepartment, updateDepartment, deleteDepartment, getStaff, getProviders } from '../utils/apiClient';

/**
 * Retrieves all departments with optional filtering
 * 
 * @param {Object} apiParams - API parameters for filtering
 * @param {string} [apiParams.departmentId] - Filter by department ID
 * @param {string} [apiParams.facilityId] - Filter by facility ID
 * @param {boolean} [apiParams.isActive] - Filter by active status
 * @returns {Promise<Array>} Array of department objects
 * 
 * @example
 * const departments = await DepartmentService.getAll({ isActive: true });
 */
export const getAll = async (apiParams = {}) => {
  console.log('deppartmentListDebugger', apiParams); 
  
  if(apiParams.facilityId) {
    if(apiParams.facilityId === 'all') {
      delete apiParams.facilityId;
    } else {
      apiParams.facilityId = apiParams.facilityId.toString();
    }
  }
  // Debug log
  try {
    const response = await getDepartments(apiParams);
    console.log('🔄 Department API Response:', response); // Debug log
    
    if (response.statusCode === "SUCCESS" && Array.isArray(response.response)) {
      console.log('✅ Departments found:', response.response.length); // Debug log
      return response.response;
    }
    
    console.warn('Unexpected response format from getDepartments:', response);
    return [];
  } catch (error) {
    console.error('Error in DepartmentService.getAll:', error);
    throw new Error('Failed to fetch departments. Please try again.');
  }
};

/**
 * Retrieves a single department by ID with optional related data
 * 
 * @param {string} departmentId - ID of the department to retrieve
 * @param {Object} options - Optional parameters
 * @param {boolean} [options.includeStaff=false] - Include staff members assigned to department
 * @param {boolean} [options.includeRenderingProviders=false] - Include rendering providers associated with department
 * @returns {Promise<Object>} Department details response
 * 
 * @example
 * const department = await DepartmentService.getById('dept-id', { includeStaff: true });
 */
export const getById = async (departmentId, options = {}) => {
  try {
    const { includeStaff = false, includeRenderingProviders = false } = options;
    const params = {};
    
    if (includeStaff) params.includeStaff = true;
    if (includeRenderingProviders) params.includeRenderingProviders = true;
    
    const response = await getDepartmentById(departmentId, params);
    console.log('🔄 Get Department By ID Response:', response); // Debug log
    
    if (response.statusCode === "SUCCESS" && response.response) {
      console.log('✅ Department details found:', response.response); // Debug log
      return {
        success: true,
        data: response.response,
        message: response.message || 'Department details retrieved successfully'
      };
    }
    
    console.warn('Unexpected response format from getDepartmentById:', response);
    return {
      success: false,
      message: response.message || 'Department not found'
    };
  } catch (error) {
    console.error('Error in DepartmentService.getById:', error);
    
    // Handle specific error cases
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'Department not found'
      };
    } else if (error.response?.status === 403) {
      return {
        success: false,
        message: 'You do not have permission to view this department'
      };
    }
    
    throw new Error('Failed to fetch department details. Please try again.');
  }
};

/**
 * Retrieves staff members assigned to a specific department
 * 
 * @param {string} departmentId - ID of the department
 * @param {Object} options - Optional parameters for filtering staff
 * @param {boolean} [options.isActive] - Filter by active status
 * @param {string} [options.facilityId] - Additional facility filter (if needed)
 * @returns {Promise<Object>} Staff members response
 * 
 * @example
 * const result = await DepartmentService.getStaffByDepartment('dept-123');
 * const activeStaff = await DepartmentService.getStaffByDepartment('dept-123', { isActive: true });
 */
export const getStaffByDepartment = async (departmentId, options = {}) => {
  try {
    const params = {
      departmentId: departmentId, // Use departmentId parameter
      ...options
    };
    
    console.log('🔍 Fetching staff for department:', departmentId, 'with params:', params);
    
    const response = await getStaff(params);
    console.log('🔄 Department Staff API Response:', response);
    
    if (response.statusCode === "SUCCESS" && Array.isArray(response.response)) {
      console.log('👥 Staff members found for department:', response.response.length);
      return {
        success: true,
        data: response.response,
        message: 'Department staff retrieved successfully'
      };
    }
    
    // Handle direct array response (some endpoints return data directly)
    if (Array.isArray(response)) {
      console.log('👥 Staff members found (direct array):', response.length);
      return {
        success: true,
        data: response,
        message: 'Department staff retrieved successfully'
      };
    }
    
    console.warn('Unexpected response format from getStaff:', response);
    return {
      success: false,
      data: [],
      message: 'No staff found for this department'
    };
  } catch (error) {
    console.error('Error in DepartmentService.getStaffByDepartment:', error);
    
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch department staff'
    };
  }
};

/**
 * Retrieves providers associated with a specific department
 * 
 * @param {string} departmentId - ID of the department
 * @param {Object} options - Optional parameters for filtering providers
 * @param {boolean} [options.isActive] - Filter by active status
 * @param {string} [options.facilityId] - Additional facility filter (if needed)
 * @returns {Promise<Object>} Providers response
 * 
 * @example
 * const result = await DepartmentService.getProvidersByDepartment('dept-123');
 * const activeProviders = await DepartmentService.getProvidersByDepartment('dept-123', { isActive: true });
 */
export const getProvidersByDepartment = async (departmentId, options = {}) => {
  try {
    const params = {
      department_id: departmentId, // Use department_id parameter (note: snake_case for providers endpoint)
      ...options
    };
    
    console.log('🔍 Fetching providers for department:', departmentId, 'with params:', params);
    
    const response = await getProviders(params);
    console.log('🔄 Department Providers API Response:', response);
    
    // getProviders returns data directly (based on apiClient implementation)
    if (Array.isArray(response)) {
      console.log('🏥 Providers found for department:', response.length);
      return {
        success: true,
        data: response,
        message: 'Department providers retrieved successfully'
      };
    }
    
    // Handle structured response if API format changes
    if (response && response.statusCode === "SUCCESS" && Array.isArray(response.response)) {
      console.log('🏥 Providers found (structured):', response.response.length);
      return {
        success: true,
        data: response.response,
        message: 'Department providers retrieved successfully'
      };
    }
    
    console.warn('Unexpected response format from getProviders:', response);
    return {
      success: false,
      data: [],
      message: 'No providers found for this department'
    };
  } catch (error) {
    console.error('Error in DepartmentService.getProvidersByDepartment:', error);
    
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch department providers'
    };
  }
};

/**
 * Creates a new department
 * 
 * @param {Object} departmentData - Department data to create
 * @param {string} departmentData.name - Department name (1-100 characters)
 * @param {string} departmentData.facilityId - Associated facility ID
 * @param {string} [departmentData.faxNumber] - Optional fax number
 * @param {boolean} [departmentData.isActive=true] - Active status
 * @returns {Promise<Object>} Created department response
 * 
 * @example
 * const result = await DepartmentService.create(departmentData);
 */
export const create = async (departmentData) => {
  try {
    const response = await createDepartment(departmentData);
    console.log('🔄 Create Department Response:', response); // Debug log
    
    if (response.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.message || 'Department created successfully',
        data: response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to create department'
    };
  } catch (error) {
    console.error('Error in DepartmentService.create:', error);
    throw new Error('Failed to create department. Please try again.');
  }
};

/**
 * Updates an existing department
 * 
 * @param {string} departmentId - ID of the department to update
 * @param {Object} departmentData - Updated department data
 * @returns {Promise<Object>} Update response
 * 
 * @example
 * const result = await DepartmentService.update('department-id', updatedData);
 */
export const update = async (departmentId, departmentData) => {
  try {
    const response = await updateDepartment(departmentId, departmentData);
    console.log('🔄 Update Department Response:', response); // Debug log
    
    if (response.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.message || 'Department updated successfully',
        data: response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to update department'
    };
  } catch (error) {
    console.error('Error in DepartmentService.update:', error);
    throw new Error('Failed to update department. Please try again.');
  }
};

/**
 * Toggles department active status
 * 
 * @param {string} departmentId - ID of the department to toggle
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Toggle response
 * 
 * @example
 * const result = await DepartmentService.toggleStatus('department-id', false);
 */
export const toggleStatus = async (departmentId, isActive) => {
  try {
    const response = await updateDepartment(departmentId, { isActive });
    
    if (response.statusCode === "SUCCESS") {
      const action = isActive ? 'activated' : 'deactivated';
      return {
        success: true,
        message: response.message || `Department ${action} successfully`,
        data: response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to update department status'
    };
  } catch (error) {
    console.error('Error in DepartmentService.toggleStatus:', error);
    throw new Error('Failed to update department status. Please try again.');
  }
};

/**
 * Deletes a department (if supported by API)
 * 
 * @param {string} departmentId - ID of the department to delete
 * @returns {Promise<Object>} Delete response
 * 
 * @example
 * const result = await DepartmentService.remove('department-id');
 */
export const remove = async (departmentId) => {
  try {
    const response = await deleteDepartment(departmentId);
    
    if (response.statusCode === "SUCCESS") {
      return {
        success: true,
        message: response.message || 'Department deleted successfully',
        data: response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to delete department'
    };
  } catch (error) {
    console.error('Error in DepartmentService.remove:', error);
    throw new Error('Failed to delete department. Please try again.');
  }
};

/**
 * Retrieves all staff members for a specific facility (not filtered by department)
 * 
 * @param {string} facilityId - ID of the facility
 * @param {Object} options - Optional parameters for filtering staff
 * @param {boolean} [options.isActive] - Filter by active status
 * @returns {Promise<Object>} All facility staff response
 * 
 * @example
 * const result = await DepartmentService.getStaffByFacility('facility-123');
 * const activeStaff = await DepartmentService.getStaffByFacility('facility-123', { isActive: true });
 */
export const getStaffByFacility = async (facilityId, options = {}) => {
  try {
    const params = {
      facilityId: facilityId,
      isActive: true,
      ...options
    };
    
    console.log('🏢 Fetching all staff for facility:', facilityId, 'with params:', params);
    
    const response = await getStaff(params);
    console.log('🔄 Facility Staff API Response:', response);
    
    if (response.statusCode === "SUCCESS" && Array.isArray(response.response)) {
      console.log('👥 Staff members found for facility:', response.response.length);
      // Filter to only include users with 'user' role (like in DepartmentAllocation)
      const userRoleStaff = response.response.filter(user => 
        user.role && Array.isArray(user.role) && user.role.some(role => role.toLowerCase() === 'user')
      );
      console.log('👤 Filtered user-role staff:', userRoleStaff.length);
      
      return {
        success: true,
        data: userRoleStaff,
        message: 'Facility staff retrieved successfully'
      };
    }
    
    // Handle direct array response
    if (Array.isArray(response)) {
      console.log('👥 Staff members found (direct array):', response.length);
      // Filter to only include users with 'user' role
      const userRoleStaff = response.filter(user => 
        user.role && Array.isArray(user.role) && user.role.some(role => role.toLowerCase() === 'user')
      );
      console.log('👤 Filtered user-role staff:', userRoleStaff.length);
      
      return {
        success: true,
        data: userRoleStaff,
        message: 'Facility staff retrieved successfully'
      };
    }
    
    console.warn('Unexpected response format from getStaff:', response);
    return {
      success: false,
      data: [],
      message: 'No staff found for this facility'
    };
  } catch (error) {
    console.error('Error in DepartmentService.getStaffByFacility:', error);
    
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch facility staff'
    };
  }
};

/**
 * Adds staff members to a department
 * 
 * @param {string} departmentId - ID of the department
 * @param {string[]} newStaffIds - Array of staff IDs to add to the department
 * @returns {Promise<Object>} Response indicating success or failure
 * 
 * @example
 * const result = await DepartmentService.addStaffToDepartment('dept-123', ['user1', 'user2']);
 */
export const addStaffToDepartment = async (departmentId, newStaffIds) => {
  try {
    console.log('👥 Adding staff to department:', departmentId, { newStaffIds });
    
    // Get current department details to get existing staff
    const deptResult = await getById(departmentId, { includeStaff: true });
    if (!deptResult.success) {
      throw new Error(deptResult.message || 'Failed to get current department staff');
    }
    
    const currentStaffIds = deptResult.data.staff 
      ? deptResult.data.staff.filter(staff => staff.is_active === true).map(staff => staff.id)
      : [];
    
    // Combine current and new staff IDs (avoid duplicates)
    const allStaffIds = [...new Set([...currentStaffIds, ...newStaffIds])];
    
    console.log('🔄 Current staff IDs:', currentStaffIds);
    console.log('➕ New staff IDs to add:', newStaffIds);
    console.log('📋 Combined staff IDs:', allStaffIds);
    
    const updateData = {
      staffIds: allStaffIds
    };
    
    const response = await updateDepartment(departmentId, updateData);
    
    console.log('✅ Staff added to department successfully:', response);
    
    return {
      success: true,
      message: `Successfully added ${newStaffIds.length} staff member(s) to department`,
      data: response
    };
  } catch (error) {
    console.error('❌ Error in addStaffToDepartment:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to add staff to department'
    };
  }
};

/**
 * Get all providers for a specific facility
 * 
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Additional query options
 * @returns {Promise<Object>} Response with providers data
 */
export const getProvidersByFacility = async (facilityId, options = {}) => {
  try {
    console.log(`🏥 Fetching providers for facility: ${facilityId}`);
    
    const params = {
      facility_id: facilityId,
      isActive: true,
      ...options
    };
    
    const response = await getProviders(params);
    
    if (response && Array.isArray(response)) {
      console.log(`✅ Found ${response.length} providers for facility ${facilityId}`);
      console.log('🔍 Debug - Facility providers service response:', response.map(p => ({
        id: p._id,
        name: p.fullName,
        affiliations: p.affiliations
      })));
      return {
        success: true,
        data: response
      };
    } else {
      console.warn(`⚠️ Unexpected provider response format:`, response);
      return {
        success: false,
        data: [],
        message: 'Invalid response format from providers API'
      };
    }
    
  } catch (error) {
    console.error(`❌ Error fetching providers for facility ${facilityId}:`, error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch providers for facility'
    };
  }
};

/**
 * Add providers to a department with roles
 * 
 * @param {string} departmentId - The department ID
 * @param {Array} renderingProviderIds - Array of provider objects with id and role
 * @param {string} renderingProviderIds[].id - Provider ID
 * @param {string[]} renderingProviderIds[].role - Array of roles for the provider
 * @returns {Promise<Object>} Response indicating success or failure
 */
export const addProvidersToDepartment = async (departmentId, renderingProviderIds) => {
  try {
    console.log(`🏥 Adding providers to department ${departmentId}:`, renderingProviderIds);
    
    // First, get current department data to preserve existing providers
    const currentDeptResult = await getById(departmentId, { 
      includeStaff: true, 
      includeRenderingProviders: true 
    });
    
    if (!currentDeptResult.success) {
      throw new Error('Failed to get current department data');
    }
    
    const currentDepartment = currentDeptResult.data;
    
    // Extract existing provider IDs with their roles
    const existingProviders = currentDepartment.renderingProviders || [];
    const existingProviderObjects = existingProviders
      .filter(provider => provider.is_active === true)
      .map(provider => ({
        id: provider._id || provider.id,
        role: provider.role || ['Provider'] // Default role if none exists
      }));
    
    console.log('🔄 Current providers in department:', existingProviderObjects);
    
    // Combine existing and new providers (avoid duplicates)
    const existingIds = existingProviderObjects.map(p => p.id);
    const newProviders = renderingProviderIds.filter(newP => !existingIds.includes(newP.id));
    const combinedProviders = [...existingProviderObjects, ...newProviders];
    
    console.log('📋 Combined provider list:', combinedProviders);
    
    // Update the department with combined provider list
    const updateData = {
      renderingProviderIds: combinedProviders
    };
    
    const updateResponse = await updateDepartment(departmentId, updateData);
    
    console.log(`✅ Successfully added ${newProviders.length} new provider(s) to department ${departmentId}`);
    
    return {
      success: true,
      data: updateResponse,
      message: `Successfully added ${newProviders.length} provider(s) to department`
    };
    
  } catch (error) {
    console.error(`❌ Error adding providers to department ${departmentId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to add providers to department'
    };
  }
};

// Default export with all methods
const DepartmentService = {
  getAll,
  getById,
  getStaffByDepartment,
  getProvidersByDepartment,
  getStaffByFacility,
  addStaffToDepartment,
  getProvidersByFacility,
  addProvidersToDepartment,
  create,
  update,
  toggleStatus,
  remove
};

export default DepartmentService;
